import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../Auth/utlis/axiosInstance';
import {
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Box,
    Alert,
    LinearProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const sessionId = searchParams.get('session_id');    const [progress, setProgress] = useState(0);
    const [confirmationStep, setConfirmationStep] = useState('Verifying payment...');

    useEffect(() => {
        let progressInterval;
        
        const confirmPayment = async () => {
            if (!sessionId) {
                setError('Invalid session ID');
                setLoading(false);
                return;
            }

            try {
                setConfirmationStep('Verifying payment...');
                setProgress(33);
                
                const response = await api.get(`/payment/success?session_id=${sessionId}`);
                
                if (response.data.success) {
                    setConfirmationStep('Payment confirmed! Updating contract status...');
                    setProgress(66);

                    // Wait a moment to show the progress
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    setConfirmationStep('Contract updated successfully!');
                    setProgress(100);

                    // Navigate after showing completion
                    setTimeout(() => {
                        // Force a refresh when returning to contracts page
                        navigate('/contracts', { state: { refresh: true } });
                    }, 1500);
                } else {
                    throw new Error('Payment verification failed');
                }
            } catch (err) {
                console.error('Payment confirmation error:', err);
                setError(err.response?.data?.message || 'Error confirming payment');
            } finally {
                setLoading(false);
                if (progressInterval) {
                    clearInterval(progressInterval);
                }
            }
        };

        confirmPayment();

        return () => {
            if (progressInterval) {
                clearInterval(progressInterval);
            }
        };
    }, [sessionId, navigate]);    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={40} thickness={4} sx={{ mb: 2 }} />
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <LinearProgress variant="determinate" value={progress} />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                        {confirmationStep}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please do not close this window...
                    </Typography>
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Alert 
                        severity="error" 
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={() => navigate('/contracts')}>
                                Go to Contracts
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        If you believe this is an error, please contact support.
                    </Typography>
                </Paper>
            </Container>
        );
    }return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                {progress < 100 ? (
                    <>
                        <CircularProgress size={60} sx={{ mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            Processing Payment
                        </Typography>
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <LinearProgress variant="determinate" value={progress} />
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            {confirmationStep}
                        </Typography>
                    </>
                ) : (
                    <>
                        <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            Payment Successful!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Your payment has been processed and the contract has been updated. 
                            You will be redirected to the contracts page shortly.
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={() => navigate('/contracts')}
                            >
                                View Contracts
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default PaymentSuccess;
